
import ManageTeams from "./ManageTeams";
import { i18nManager } from "../../../i18n";
import { Schemes, Share } from "../../../utils";
import * as ReactRouterDom from "react-router-dom";
import TeamNotifications from "./TeamNotifications";
import TeamSettings from "./settings/TeamSettings";
import TeamTransactions from "./analytics/TeamTransactions";
import { ConfirmDialog, Siren } from "../../shared/ConfirmDialog";
import TeamGeneralAnalytics from "./analytics/TeamGeneralAnalytics";
import TeamEarningAnalytics from "./analytics/TeamEarningAnalytics";
import TeamTournamentAnalytics from "./analytics/TeamTournamentAnalytics";
import TeamAchievementAnalytics from "./analytics/TeamAchievementAnalytics";
import { Alignment, AlertInterface, DateTimeInput, DOMHelper, useSubscription } from "@rivtn/noseur";
import { BaseService, IMember, ITeamInvite, SanitizedResponse, TeamService } from "../../../services";

export interface TeamsProps {
    profile: IMember;
}

function Teams(props: TeamsProps) {

    const { profile } = props;
    const labels = i18nManager.Labels.teams;
    const teamService = TeamService.getInstance();
    useSubscription("TEAM.VIEW_JOIN_CODE", shareJoinCode);

    return (<ReactRouterDom.Routes>
        <ReactRouterDom.Route path="/" element={<ManageTeams profile={profile} />} />
        <ReactRouterDom.Route path="/:id/notifications" element={<TeamNotifications />} />
        <ReactRouterDom.Route path="/:id/settings" element={<TeamSettings profile={profile} />} />
        <ReactRouterDom.Route path="/:id/analytics" element={<TeamGeneralAnalytics profile={profile} />} />
        <ReactRouterDom.Route path="/:id/analytics/earnings" element={<TeamEarningAnalytics profile={profile} />} />
        <ReactRouterDom.Route path="/:id/analytics/transactions" element={<TeamTransactions profile={profile} />} />
        <ReactRouterDom.Route path="/:id/analytics/tournaments" element={<TeamTournamentAnalytics profile={profile} />} />
        <ReactRouterDom.Route path="/:id/analytics/achievements" element={<TeamAchievementAnalytics profile={profile} />} />
    </ReactRouterDom.Routes>);

    function shareJoinCode(teamInvite: Partial<ITeamInvite>) {
        const activeTeam = teamInvite.team!;
        const joinCodeElement = { current: null } as any;
        let joinCode: string = teamInvite.join_code ?? "";
        const dialogLabels = labels.dialog.share_join_code;
        let expiryDate: string = teamInvite.expiry_date ?? "";
        const dialog = ConfirmDialog({
            style: { maxWidth: 400 },
            title: dialogLabels.title,
            footerAlignment: Alignment.RIGHT,
            confirmLabel: i18nManager.Labels.common.close,
            desc: dialogLabels.desc.replace("${team_name}", activeTeam.name),
            onLoading: async (alert: AlertInterface) => {
                if (teamInvite.join_code) {
                    return true;
                }
                teamService.createOrUpdateJoinCode(activeTeam.external_id, joinCode, expiryDate).then(({ sanitized }: SanitizedResponse<ITeamInvite>) => {
                    joinCode = sanitized.join_code;
                    alert.doneLoading(() => {
                        joinCodeElement.current.innerText = sanitized.join_code;
                    });
                }).catch((error: SanitizedResponse<any>) => {
                    alert.destroy(() => BaseService.reportError(error));
                });
                return false;
            },
            content: (<div style={{ margin: "10px 0px", display: "flex", flexDirection: "column" }}>
                <div style={{ maxWidth: 180, wordWrap: "break-word", margin: "30px auto", fontWeight: "bold" }} ref={joinCodeElement}>{joinCode}</div>
                <DateTimeInput fill scheme={Schemes.OMNEHOST} disableToDate={new Date()} selectedDates={expiryDate ? [new Date(expiryDate)] : undefined}
                    formControlProps={{
                        isFieldset: true,
                        label: dialogLabels.expiry_date.title,
                        rightContent: dialogLabels.expiry_date.icon,
                    }} onSelectDate={(o) => {
                        teamService.createOrUpdateJoinCode(activeTeam.external_id, joinCode, o.selectedDate!.toUTCString()).then(({ sanitized }: SanitizedResponse<ITeamInvite>) => {
                            Siren.alert(dialogLabels.expiry_updated);
                        }).catch(BaseService.reportError);
                    }} />
                <span style={{ marginTop: 15, marginBottom: 10 }}>{labels.dialog.share_to}</span>
                <div className="d-socials" style={{ display: "flex" }}>
                    <i className="fa fa-comment" />
                    <i className="fab fa-x-twitter" onClick={() => Share.twitter(joinCode)} />
                    <i className="fab fa-instagram" style={{ color: "#c46b54" }} onClick={() => Share.instagram(joinCode)} />
                    <i className="fab fa-whatsapp" style={{ color: "#33b544" }} onClick={() => Share.whatsapp(joinCode)} />
                    <i className="fab fa-facebook" style={{ color: "#2c5ca3" }} onClick={() => Share.facebook(joinCode)} />
                </div>
                <span className="link" style={{ marginTop: 15 }} onClick={() => DOMHelper.copyToClipboard(joinCode, () => {
                    Siren.alert(i18nManager.Labels.common.copied, { lifetime: 1000 });
                })}>{dialogLabels.copy_join_code}</span>
            </div>),
        });
        dialog.show();
    }

}

export default Teams;
